import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { useHypoteseConfig, useHypoteseMedNummer, useMineHypoteser, type HypothesisSummary } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Fot, Tekstknapp, Varsel } from './deler.tsx';
import { HypRad } from './HListe.tsx';
import { Sporsmaalskort } from './HypotesenMin.tsx';
import { datoMedAar, erAvsluttet, isoDag, mineIRekkefolge, navnAv, slettesSnart } from './lesning.ts';

/**
 * «Mine hypoteser» — forsiden for bidragsyter (W7; M10a). Øverst ETT kort for det som krever
 * deg: spørsmålet etter «Trenger mer», med hypotesens tittel i overskriften fordi lista står under,
 * og «Endre hypotesen» som eneste fylte knapp. Så tre grupper i fast rekkefølge — Utkast · Sendt ·
 * Avsluttet — og en tom gruppe står ikke.
 *
 * Raden er registerets (DD-61): statusens ikon, tittelen, ETT tall til høyre — fristen halvfet der
 * den er et løfte, «tas opp igjen»-datoen i Parkert, den betingede datoen i Mottatt uten halvfet —
 * og meta-linjen med status, det som gjelder, og nummeret. «venter på deg» står halvfet når et
 * spørsmål er åpent. Raden er en lenke: hypotesen er en egen side (W8), ikke en detalj ved siden av.
 * j/k flytter mellom radene, ↵ åpner (DD-11).
 *
 * Et utkast ingen har endret, slettes etter 180 dager. De siste 14 dagene står «Slettes [dato]»
 * på raden — tjenerens `draftDeletesOn` i panelets kortform, aldri «om N dager» (DD-28), og med
 * ikon ved teksten, så farge aldri bærer meningen alene.
 */
export function MineSide() {
  const smal = useSmal();
  const naviger = useNavigate();
  const mine = useMineHypoteser();
  const config = useHypoteseConfig();
  const megId = useMe().data?.id;
  const liste = useRef<HTMLUListElement>(null);

  const utkast = mine.data?.filter((h) => h.status === 'utkast') ?? [];
  const innsendt = mineIRekkefolge(mine.data ?? []);
  const sendt = innsendt.filter((h) => !erAvsluttet(h));
  const avsluttet = innsendt.filter(erAvsluttet);
  const tom = mine.isSuccess && mine.data.length === 0;
  const iDag = isoDag(new Date());
  const slettes = (h: HypothesisSummary) => (h.draftDeletesOn !== undefined && slettesSnart(h.draftDeletesOn, iDag) ? `Slettes ${datoMedAar(h.draftDeletesOn, iDag)}` : null);

  // Det som krever deg: den første som venter på deg. Spørsmålet står i hypotesen, ikke på raden.
  const venter = sendt.find((h) => h.awaitingSubmitter);
  const kreverDeg = useHypoteseMedNummer(venter?.number ?? null);
  const sporsmaal = kreverDeg.data?.awaitingSubmitter ? [...kreverDeg.data.responses].reverse().find((r) => r.kind === 'trenger_mer') : undefined;

  // j/k flytter mellom radene i lista (DD-11); ↵ på en lenke åpner. Aldri i et felt.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const maal = e.target as HTMLElement | null;
      if (maal && /^(INPUT|TEXTAREA|SELECT)$/.test(maal.tagName)) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented || (e.key !== 'j' && e.key !== 'k')) return;
      const rader = [...(liste.current?.querySelectorAll<HTMLElement>('[data-mine-rad]') ?? [])];
      if (rader.length === 0) return;
      e.preventDefault();
      const i = rader.indexOf(document.activeElement as HTMLElement);
      const til = i === -1 ? 0 : e.key === 'j' ? Math.min(rader.length - 1, i + 1) : Math.max(0, i - 1);
      rader[til]?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const rad = (h: HypothesisSummary) => <HypRad key={h.id} h={h} config={config.data} megId={megId} valgt={false} smal={smal} mine til={`/h/${h.number}`} />;

  let innhold;
  if (mine.isPending) {
    innhold = (
      <div role="status" aria-busy="true" aria-label="Laster" className="panel-skjelett">
        <span className="panel-skjelett-linje panel-skjelett-tittel" />
        <span className="panel-skjelett-linje" />
      </div>
    );
  } else if (mine.isError) {
    innhold = <Varsel handling={<Tekstknapp onClick={() => void mine.refetch()}>Prøv igjen</Tekstknapp>}>Fikk ikke hentet hypotesene dine. Det du har sendt inn er ikke borte.</Varsel>;
  } else if (tom) {
    // DD-19: tomtilstanden inviterer — én setning og «Ny hypotese».
    innhold = <p className="h-tekst-dempet">Det du sender inn står her med svar og dato.</p>;
  } else {
    innhold = (
      <>
        {venter && sporsmaal ? <Sporsmaalskort h={venter} sporsmaal={sporsmaal} medTittel testid="hm-krever" /> : null}
        <ul className="h-liste" ref={liste}>
          {utkast.length > 0 ? <li className="h-gruppe">Utkast</li> : null}
          {utkast.map((h) => {
            const varsel = slettes(h);
            return (
              <li key={h.id}>
                <Link to={`/h/utkast/${h.id}`} className="h-rad" data-mine-rad="" aria-label={`Utkast: ${h.title ?? 'Uten tittel'}.${h.sourceQuestionAnsweredBy ? ` ${navnAv(h.sourceQuestionAnsweredBy)} har svart.` : ''}${varsel ? ` ${varsel}.` : ''} Fortsett.`}>
                  <Ikon navn="document-outline" className="h-rad-ikon" />
                  <span className="h-rad-tittel">{h.title ?? 'Uten tittel'}</span>
                  <span className="h-rad-hoyre h-rad-lenke">Fortsett</span>
                  <span className="h-rad-meta">
                    <b>{config.data?.statusWording.find((w) => w.key === 'utkast')?.label ?? 'Utkast'}</b>
                    {/* H3.3 (W8): kildens eier har svart, og utkastet venter på at innsenderen velger. */}
                    {h.sourceQuestionAnsweredBy ? (
                      <span className="h-rad-svart">
                        <Ikon navn="help-circle-outline" />
                        {navnAv(h.sourceQuestionAnsweredBy)} har svart
                      </span>
                    ) : null}
                    {varsel ? (
                      <span className="h-slettes" data-testid="h-slettes">
                        <Ikon navn="trash-outline" className="h-slettes-ikon" />
                        {varsel}
                      </span>
                    ) : null}
                  </span>
                </Link>
              </li>
            );
          })}
          {sendt.length > 0 ? <li className="h-gruppe">Sendt</li> : null}
          {sendt.map(rad)}
          {avsluttet.length > 0 ? <li className="h-gruppe">Avsluttet</li> : null}
          {avsluttet.map(rad)}
        </ul>
      </>
    );
  }

  return (
    <div className="h-flate hm-mine">
      <div className="h-rull">
        <div className="h-innhold">
          <h1 className="panel-h1">{tom ? 'Ingen hypoteser ennå' : 'Mine hypoteser'}</h1>
          {innhold}
        </div>
      </div>
      {/* «Ny hypotese» er primærknappen i foten under 900, sidemenyens knapp over (DD-21, DD-19). */}
      {smal ? <Fot primar={{ label: 'Ny hypotese', onClick: () => void naviger('/h/ny'), ikon: 'add-outline', tast: 'ingen', testid: 'h-ny-fot' }} /> : null}
    </div>
  );
}
